// src/client/router.tsx
//
// Phase B' T10: route-based code splitting (React.lazy).
//
//  - 各 page を React.lazy で動的 import → 別 chunk として split
//  - AppShell の <Outlet /> 周辺に <Suspense> を入れて初回 chunk load 中の fallback
//  - Login (public) と Home / ProjectList / ProjectDetail / etc (protected) を分離
//
//  bundle 効果:
//    - 初回 load (login 画面など) で ProjectDetail の重い chunk (8 tab + dialogs) を読まない
//    - Phase B'' / B2 / B3 で route 追加しても既存 chunk size に影響しない設計基盤

import { lazy, Suspense } from "react";
import { createBrowserRouter } from "react-router-dom";
import { ProtectedRoute } from "./components/ProtectedRoute";
import { RouteErrorBoundary } from "./components/RouteErrorBoundary";
import { RouteFallback } from "./components/RouteFallback";
import { AppShell } from "./layouts/AppShell";

const Login = lazy(() => import("./pages/Login"));
const AcceptInvite = lazy(() => import("./pages/AcceptInvite"));
const ChangePassword = lazy(() => import("./pages/ChangePassword"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const ProjectList = lazy(() => import("./pages/ProjectList"));
// Phase 13c D-6: /projects/new は ProjectNewWizard (5-step) に切替済。 旧 ProjectNew は削除。
const ProjectNewWizard = lazy(() => import("./pages/ProjectNewWizard"));
const ProjectDetail = lazy(() => import("./pages/ProjectDetail"));
const ProjectEdit = lazy(() => import("./pages/ProjectEdit"));
const CustomerNew = lazy(() => import("./pages/CustomerNew"));
const Customers = lazy(() => import("./pages/Customers"));
const CustomerDetail = lazy(() => import("./pages/CustomerDetail"));
const Properties = lazy(() => import("./pages/Properties"));
const PropertyNew = lazy(() => import("./pages/PropertyNew"));
const PropertyDetail = lazy(() => import("./pages/PropertyDetail"));
const AdminMilestoneDefinitions = lazy(
  () => import("./pages/AdminMilestoneDefinitions"),
);
const AdminProcessTemplates = lazy(
  () => import("./pages/AdminProcessTemplates"),
);
const AdminVendors = lazy(() => import("./pages/AdminVendors"));
const AdminVendorBillings = lazy(() => import("./pages/AdminVendorBillings"));
const AdminVendorPayments = lazy(() => import("./pages/AdminVendorPayments"));
const AdminFinanceDashboard = lazy(() => import("./pages/AdminFinanceDashboard"));
// 段 4-C (#706): 経理の高密度一覧 (読むだけ。 旧 admin/vendor-* 画面は残す)
const FinanceLedger = lazy(() => import("./pages/FinanceLedger"));
// gap-b2b (#685 F07): 支払の実行 (振込の束)。 /finance/:view? より先に置く (固定の path を優先)
const FinancePaymentRuns = lazy(() => import("./pages/FinancePaymentRuns"));
// gap-g3 (#686 F08): 会計出力・取込結果の照合・訂正の前後・証憑の検索。 /finance/:view? より先に置く
const FinanceAccounting = lazy(() => import("./pages/FinanceAccounting"));
// gap-g2 (#684 F06): 業者から届いた請求書の確認・差戻し・受理。 /finance/:view? より先に置く
const FinanceVendorInvoices = lazy(() => import("./pages/FinanceVendorInvoices"));
// gap-i1 (#685): 振込口座 (経理が取引先・自社の口座を登録・変更する入口)。 /finance/:view? より先に置く
const FinanceBankAccounts = lazy(() => import("./pages/FinanceBankAccounts"));
const AdminAiQuotas = lazy(() => import("./pages/AdminAiQuotas"));
const AdminCustomerLineCandidates = lazy(() => import("./pages/AdminCustomerLineCandidates"));
const AdminCustomerProjectLinks = lazy(() => import("./pages/AdminCustomerProjectLinks"));
const AdminDocumentClassificationCandidates = lazy(() => import("./pages/AdminDocumentClassificationCandidates"));
const AdminSiteCheckinQrCodes = lazy(() => import("./pages/AdminSiteCheckinQrCodes"));
const AdminStandardPhotoTemplates = lazy(() => import("./pages/AdminStandardPhotoTemplates"));
// gap-g3 (#688 F10): 検査テンプレート (項目・基準・版。 下書き → 公開)
const AdminInspectionTemplates = lazy(() => import("./pages/AdminInspectionTemplates"));
const AdminFieldWorkerCandidates = lazy(() => import("./pages/AdminFieldWorkerCandidates"));
const AdminLineAccounts = lazy(() => import("./pages/AdminLineAccounts"));
const AdminLineUserCodes = lazy(() => import("./pages/AdminLineUserCodes"));
const AdminKyChecklists = lazy(() => import("./pages/AdminKyChecklists"));
const AdminUsers = lazy(() => import("./pages/AdminUsers"));
// 段 3-A: 外部 AI (ChatGPT / Claude) の接続登録 (tenant_admin)
const AdminOAuthClients = lazy(() => import("./pages/AdminOAuthClients"));
// gap-a2: 運用設定 (#702) / LINE 送信の失敗 (#703) / 未請求の検収残 (#673)。 新しい URL を足すだけ
const AdminOrganization = lazy(() => import("./pages/AdminOrganization"));
const AdminRoles = lazy(() => import("./pages/AdminRoles"));
const AdminWorkflowSteps = lazy(() => import("./pages/AdminWorkflowSteps"));
const AdminFeatureFlags = lazy(() => import("./pages/AdminFeatureFlags"));
const AdminLineSendFailures = lazy(() => import("./pages/AdminLineSendFailures"));
const AdminVendorUnbilled = lazy(() => import("./pages/AdminVendorUnbilled"));
const DigitalSignboardCamera = lazy(() => import("./pages/DigitalSignboardCamera"));
const WorkSiteInbox = lazy(() => import("./pages/WorkSiteInbox"));
const WorkFinanceInbox = lazy(() => import("./pages/WorkFinanceInbox"));
const WorkReviewInbox = lazy(() => import("./pages/WorkReviewInbox"));
// gap-k8 目的 1: 工程会議 (全案件のマイルストーン・工程、 読むだけ)。 新しい URL を足すだけ
const ScheduleMilestoneBoard = lazy(() => import("./pages/ScheduleMilestoneBoard"));
// gap-k8 目的 2: 粗利の推移 (見積 → 契約 → 実行予算 → 実績、 読むだけ)
const ProfitTransition = lazy(() => import("./pages/ProfitTransition"));
// MCP 認可層 段 B: 外部 AI への権限付与の同意画面 (既存 ProtectedRoute の中 = 新しい認証経路を作らない)
const OAuthConsent = lazy(() => import("./pages/OAuthConsent"));
// 段 4-A (#704): 案件中心の 4 列の画面。 新しい URL に足すだけで、 旧 route は 1 本も変えない (D34)
const ProjectHub = lazy(() => import("./pages/ProjectHub"));

// Phase 12b LIFF (P-K) — LINE 内 webview 専用、 AppShell / ProtectedRoute は使わない (auth 経路別)
const LiffLayout = lazy(() => import("./pages/liff/Layout"));
const LiffCheckin = lazy(() => import("./pages/liff/Checkin"));
const LiffKy = lazy(() => import("./pages/liff/Ky"));
// Phase 1: 施工業者が発注書を受注する (受注書を返す)
const LiffOrders = lazy(() => import("./pages/liff/Orders"));
// gap-g4 (#697): 顧客ポータル (LINE 内)。 認証は同じ LiffLayout (LINE ログイン)、 API は既存の /api/v1/liff/customer
const LiffCustomerPortal = lazy(() => import("./pages/liff/CustomerPortal"));
// 案 A (document-pdf-output-plan): 発注書 / 受注書の印刷用。 AppShell の chrome を印刷に混ぜない
const PrintPurchaseOrder = lazy(() => import("./pages/PrintPurchaseOrder"));
// gap-i1 (#680 / #683): 顧客に出す見積書・請求書の印刷画面 (PDF は作らない、 ブラウザの 「PDF として保存」)
const PrintCustomerEstimate = lazy(() => import("./pages/PrintCustomerEstimate"));
const PrintCustomerInvoice = lazy(() => import("./pages/PrintCustomerInvoice"));
// gap-k8 目的 3: 定期点検 (アフター) の報告書の印刷画面 (既存の GET だけを読む)
const PrintAftercareReport = lazy(() => import("./pages/PrintAftercareReport"));
// 段 4-B (#705): 現場スマホ (新しい URL /m/...。 旧画面は変えない。 画面の中の route は pages/field/FieldMobile.tsx)
const FieldMobile = lazy(() => import("./pages/field/FieldMobile"));

export const router = createBrowserRouter([
  {
    path: "/",
    element: <AppShell />,
    // Phase post-13a: chunk load 失敗 (deploy 直後の旧 index.html) を自動回復
    errorElement: <RouteErrorBoundary />,
    children: [
      // public
      { path: "login", element: <Login /> },
      { path: "accept-invite/:token", element: <AcceptInvite /> },
      { path: "reset-password/:token", element: <ResetPassword /> },
      { path: "forgot-password", element: <ForgotPassword /> },
      // protected
      {
        element: <ProtectedRoute />,
        children: [
          { index: true, element: <Dashboard /> },
          { path: "dashboard", element: <Dashboard /> },
          { path: "projects", element: <ProjectList /> },
          { path: "projects/new", element: <ProjectNewWizard /> },
          { path: "projects/:projectId", element: <ProjectDetail /> },
          { path: "projects/:projectId/edit", element: <ProjectEdit /> },
          { path: "customers/new", element: <CustomerNew /> },
          { path: "customers", element: <Customers /> },
          { path: "customers/:customerId", element: <CustomerDetail /> },
          { path: "properties", element: <Properties /> },
          { path: "properties/new", element: <PropertyNew /> },
          { path: "properties/:propertyId", element: <PropertyDetail /> },
          { path: "admin/milestone-definitions", element: <AdminMilestoneDefinitions /> },
          { path: "admin/process-templates", element: <AdminProcessTemplates /> },
          { path: "admin/vendors", element: <AdminVendors /> },
          { path: "admin/vendor-billings", element: <AdminVendorBillings /> },
          { path: "admin/vendor-payments", element: <AdminVendorPayments /> },
          { path: "admin/finance-dashboard", element: <AdminFinanceDashboard /> },
          { path: "finance/payment-runs", element: <FinancePaymentRuns /> },
          { path: "finance/vendor-invoices", element: <FinanceVendorInvoices /> },
          { path: "finance/bank-accounts", element: <FinanceBankAccounts /> },
          { path: "finance/accounting", element: <FinanceAccounting /> },
          { path: "finance/:view?", element: <FinanceLedger /> },
          { path: "admin/ai-quotas", element: <AdminAiQuotas /> },
          { path: "admin/customer-line-candidates", element: <AdminCustomerLineCandidates /> },
          { path: "admin/customer-project-links", element: <AdminCustomerProjectLinks /> },
          { path: "admin/document-classification-candidates", element: <AdminDocumentClassificationCandidates /> },
          { path: "admin/site-checkin-qr-codes", element: <AdminSiteCheckinQrCodes /> },
          { path: "admin/standard-photo-templates", element: <AdminStandardPhotoTemplates /> },
          { path: "admin/inspection-templates", element: <AdminInspectionTemplates /> },
          { path: "admin/field-worker-candidates", element: <AdminFieldWorkerCandidates /> },
          { path: "admin/line-accounts", element: <AdminLineAccounts /> },
          { path: "admin/line-user-codes", element: <AdminLineUserCodes /> },
          { path: "admin/ky-checklists", element: <AdminKyChecklists /> },
          { path: "admin/users", element: <AdminUsers /> },
          { path: "admin/oauth-clients", element: <AdminOAuthClients /> },
          { path: "admin/organization", element: <AdminOrganization /> },
          { path: "admin/roles", element: <AdminRoles /> },
          { path: "admin/workflow-step-definitions", element: <AdminWorkflowSteps /> },
          { path: "admin/feature-flags", element: <AdminFeatureFlags /> },
          { path: "admin/line-send-failures", element: <AdminLineSendFailures /> },
          { path: "admin/vendor-billings/unbilled", element: <AdminVendorUnbilled /> },
          { path: "change-password", element: <ChangePassword /> },
          { path: "digital-signboard-camera", element: <DigitalSignboardCamera /> },
          { path: "work/site", element: <WorkSiteInbox /> },
          { path: "work/finance", element: <WorkFinanceInbox /> },
          { path: "work/review", element: <WorkReviewInbox /> },
          { path: "schedule/milestones", element: <ScheduleMilestoneBoard /> },
          { path: "sales/profit-transition", element: <ProfitTransition /> },
          // request は query ではなく path に載せる (ProtectedRoute が保存するのは pathname だけのため)
          { path: "oauth/consent/:request", element: <OAuthConsent /> },
          // 段 4-A (#704): 案件中心の 4 列 (/p と /p/:projectId/:mode)。 旧 /projects/* は併存 (D34)
          { path: "p", element: <ProjectHub /> },
          { path: "p/:projectId/:mode?", element: <ProjectHub /> },
        ],
      },
    ],
  },
  // 印刷用 route group (認証必須、 AppShell 不使用 = nav が印刷に混ざらない)
  {
    path: "/print",
    element: <ProtectedRoute />,
    errorElement: <RouteErrorBoundary />,
    children: [
      {
        path: "projects/:projectId/purchase-orders/:purchaseOrderId",
        element: <PrintPurchaseOrder />,
      },
      {
        path: "projects/:projectId/estimates/:estimateId",
        element: <PrintCustomerEstimate />,
      },
      {
        path: "projects/:projectId/customer-invoices/:invoiceId",
        element: <PrintCustomerInvoice />,
      },
      {
        path: "projects/:projectId/aftercare/:scheduleId",
        element: <PrintAftercareReport />,
      },
    ],
  },
  // 段 4-B (#705): 現場スマホ (認証必須、 AppShell 不使用 = スマホの 1 画面 1 目的の枠)
  {
    path: "/m",
    element: <ProtectedRoute />,
    errorElement: <RouteErrorBoundary />,
    // react-router v7 の splat (*) は親の /m そのものには一致しないので index も置く (無いと /m が空白になる)
    children: [
      { index: true, element: <Suspense fallback={<RouteFallback />}><FieldMobile /></Suspense> },
      { path: "*", element: <Suspense fallback={<RouteFallback />}><FieldMobile /></Suspense> },
    ],
  },
  // Phase 12b LIFF route group (LINE 内 webview、 LIFF SDK auth、 AppShell 不使用)
  {
    path: "/liff",
    element: <LiffLayout />,
    errorElement: <RouteErrorBoundary />,
    children: [
      { path: "checkin", element: <LiffCheckin /> },
      { path: "ky", element: <LiffKy /> },
      { path: "orders", element: <LiffOrders /> },
      { path: "customer", element: <LiffCustomerPortal /> },
    ],
  },
]);
